import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Inbox, Search } from "lucide-react";
import api from "@/lib/api";
import { PartnerShell } from "./Dashboard";

const SOURCE_LABEL = {
  partner_link: { label: "Partner link",        bg: "bg-emerald-100", text: "text-emerald-800" },
  stored_partner_attribution: { label: "Stored attribution", bg: "bg-amber-100",   text: "text-amber-800" },
  region_allocation: { label: "Region allocated", bg: "bg-indigo-100", text: "text-indigo-800" },
};

const LAB_STATUS_LABEL = {
  pending_sample: "Awaiting sample",
  awaiting_drop_off: "Awaiting drop-off",
  received: "Received",
  results_entered: "Results entered",
  submitted_for_review: "In review",
  in_review: "In review",
  awaiting_review: "In review",
  approved: "Approved",
  published: "Published",
  failed: "Failed",
};

const TEST_LABEL = {
  basic: "AWARE 4-Point",
  tank: "AWARE Tank 360",
  bore: "AWARE Bore 360",
  basic_water_test: "AWARE 4-Point",
  comprehensive_water_test: "AWARE Tank 360",
  bore_water_comprehensive: "AWARE Bore 360",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

export default function PartnerBookings() {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (sourceFilter) params.set("booking_source", sourceFilter);
    if (q) params.set("q", q);
    const qs = params.toString();
    const url = `/partner/bookings${qs ? `?${qs}` : ""}`;
    (async () => {
      try {
        const { data } = await api.get(url);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Unable to load bookings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceFilter, q]);

  return (
    <PartnerShell active="bookings">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Partner Bookings</div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="partner-bookings-heading">Your allocated bookings</h1>
        <div className="text-sm text-slate-600 mt-1" data-testid="partner-bookings-total">
          {loading ? "Loading…" : `${data.total} booking${data.total === 1 ? "" : "s"} attributed to your franchisee`}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="partner-bookings-filter-bar">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, address or booking #…"
            data-testid="partner-bookings-search"
            className="w-64 rounded-full border border-slate-300 bg-white pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-cyan-500"
          />
        </div>
        {[
          { key: "", label: "All" },
          { key: "partner_link", label: "Partner link" },
          { key: "stored_partner_attribution", label: "Stored attribution" },
          { key: "region_allocation", label: "Region allocated" },
        ].map((f) => {
          const active = sourceFilter === f.key;
          return (
            <button
              key={f.key || "all"}
              type="button"
              onClick={() => setSourceFilter(f.key)}
              data-testid={`partner-bookings-filter-${f.key || "all"}`}
              className={`inline-flex items-center text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-rose-800" data-testid="partner-bookings-error">
          {error}
        </div>
      )}

      {!error && !loading && data.rows.length === 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center" data-testid="partner-bookings-empty">
          <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-sm font-semibold text-slate-700">{q ? "No bookings match your search" : "No bookings yet"}</div>
          <div className="text-xs text-slate-500 mt-1">
            {q ? "Try a different customer name, address or booking number." : "Bookings attributed to your franchisee will appear here."}
          </div>
        </div>
      )}

      {!error && data.rows.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="partner-bookings-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Test</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Lab status</th>
                  <th className="px-4 py-3">Report</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="partner-bookings-rows">
                {data.rows.map((b) => {
                  const src = SOURCE_LABEL[b.booking_source] || { label: "Direct", bg: "bg-slate-100", text: "text-slate-700" };
                  const labLabel = LAB_STATUS_LABEL[b.lab_status] || (b.lab_status ? b.lab_status.replace(/_/g, " ") : "—");
                  return (
                    <tr key={b.reference} className="hover:bg-slate-50" data-testid={`partner-booking-row-${b.reference}`}>
                      <td className="px-4 py-3 text-slate-700" data-testid={`partner-booking-${b.reference}-date`}>{fmtDate(b.created_at)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900" data-testid={`partner-booking-${b.reference}-customer`}>{b.customer?.name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[260px] truncate" data-testid={`partner-booking-${b.reference}-address`}>{b.customer?.address || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`partner-booking-${b.reference}-test`}>{TEST_LABEL[b.test_id] || b.test_name || b.test_id || "—"}</td>
                      <td className="px-4 py-3" data-testid={`partner-booking-${b.reference}-source`}>
                        <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${src.bg} ${src.text}`}>
                          {src.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap" data-testid={`partner-booking-${b.reference}-lab-status`}>{labLabel}</td>
                      <td className="px-4 py-3 whitespace-nowrap" data-testid={`partner-booking-${b.reference}-report-status`}>
                        {b.report_available ? (
                          <Link
                            to={`/partner/bookings/${b.reference}`}
                            className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          >
                            Available
                          </Link>
                        ) : b.lab_status ? (
                          <span className="text-xs text-slate-500">Awaiting lab review</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/partner/bookings/${b.reference}`}
                          data-testid={`partner-booking-${b.reference}-view`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-800 hover:underline"
                        >
                          View <ArrowRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PartnerShell>
  );
}
